/**
 * CouncilsCorner Page Component - By John Bellew - Infinia.ie - last updated 15/07/2025
 * 
 * A comprehensive page for displaying council corner information with:
 * - Members Council section with detailed member profiles
 * - IF 2024-2027 Strategy Dashboard with strategic objectives
 * - Monthly Round-Up with latest council activities
 * - Reference Documents section for important resources
 * - Dynamic date formatting for monthly content
 * - Section dividers for clear content organization
 * - Loading states for smooth user experience
 * - Responsive design with gradient backgrounds
 * 
 * Features:
 * - Dynamic data fetching from multiple JSON sources
 * - Latest month calculation for round-up displays
 * - Strategic dashboard visualization
 * - Document management integration
 * - Cross-browser compatibility
 * 
 * @returns {JSX.Element} CouncilsCorner page component
 */
import React, { useEffect, useState } from 'react';
import Divider from '../components/Common/Divider';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';
import MembersCouncil from '../components/CouncilCorner/MembersCouncil';
import CouncilStrategy from '../components/CouncilCorner/CouncilStrategy';
import MonthlyRoundup from '../components/CouncilCorner/MonthlyRoundup';
import CCReferenceDocs from '../components/CouncilCorner/CCReferenceDocs';

const { FILE_NAMES } = require('../utils/constants');

/**
 * CouncilsCorner component for displaying comprehensive council information
 * including members, strategy, monthly updates, and reference documents
 */
function CouncilsCorner() {
  const [membersCouncilData, setMembersCouncilData] = useState([]);
  const [councilStrategyData, setCouncilStrategyData] = useState([]);
  const [monthlyData, setMonthlyData] = useState([]);
  const [councilReferenceDocsData, setCouncilReferenceDocsData] = useState([]);
  const [latestMonth, setLatestMonth] = useState('');
  const strategyTitle = `IF 2024 - 2027 Strategy Dashboard`;

  useEffect(() => {
    const fetchData = async () => {
      try {
        const membersCouncilDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MEMBERS_COUNCIL);
        setMembersCouncilData(membersCouncilDataResult.fileContent);
        const councilStrategyDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.COUNCIL_STRATEGY);
        setCouncilStrategyData(councilStrategyDataResult.fileContent);
        const monthlyDataResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.MONTHLY_ROUNDUP);
        setMonthlyData(monthlyDataResult.fileContent);
        const councilReferenceDocsResult = await apiHelper.fetchJSONFileByNameAndContent(FILE_NAMES.COUNCIL_REFERENCE_DOCS);
        setCouncilReferenceDocsData(councilReferenceDocsResult.fileContent);
        
        // Extract the latest date from monthlyData
        if (monthlyDataResult.fileContent && monthlyDataResult.fileContent.length > 0) {
          const latestDate = new Date(Math.max(...monthlyDataResult.fileContent.map(item => new Date(item.date))));
          const month = latestDate.toLocaleString('default', { month: 'long' });
          const year = latestDate.getFullYear();
          setLatestMonth(`${month} ${year}`);
        }
      } catch (err) {
        console.error('Error fetching council corner data:', err);
      }
    };
    fetchData();
  }, []);

  return (
    <div className='totalPage'>
      <section className="wrapper">
        <div id="memberscouncil"  className="container mt-6 mb-6">
          <h2 className="text-uppercase main-header">Members Council</h2>
        </div>     
        <div className="container">
          {membersCouncilData.length > 0 ? (
            <MembersCouncil data={membersCouncilData} />
          ) : (
            <LoadingSpinner text="Loading Members Council" />
          )}
        </div>
      </section>

      <Divider height={60} />

      <section className="wrapper bg-gradient-primary mb-10">
        <div id="strategy" className="container pb-6">
          <h2 className="text-uppercase main-header">{strategyTitle}</h2>
        </div>
        <div className="container">
          {councilStrategyData.length > 0 ? (
            <CouncilStrategy data={councilStrategyData} />
          ) : (
            <LoadingSpinner text="Loading Council Strategy" />
          )}
        </div>
      </section>

      <Divider height={60} />

      <section className="wrapper bg-gradient-primary mb-10">
        <div id="roundup" className="container mb-6">
        {latestMonth && (
          <h2 className="text-uppercase main-header">{latestMonth} Round-Up</h2>
          )}
        </div>
        <div className="container">
          <MonthlyRoundup data={monthlyData} />
        </div>
      </section>
      <Divider height={60} />
      <section className="wrapper bg-gradient-primary mb-10">
        
        <div className="container">
        <div id="reference-docs" className="container mb-6">
          <h2 className="text-uppercase main-header">Reference Documents</h2>
        </div>
          <CCReferenceDocs data={councilReferenceDocsData} />
        </div>
      </section>
    </div>
  );
}

export default CouncilsCorner;